import React, { useEffect, useState, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill, { Quill } from 'react-quill';
import { useAuth } from '../../context/AuthContext';

import icon from '../../assets/support-icon3.svg';
import uploadBtn from '../../assets/fileUpload.svg';
import clip from '../../assets/clip.svg';
import fileIcon from '../../assets/fileIcon.svg';
import closeIcon from '../../assets/closeIcon.svg';

interface NoticeFormData {
  user: string;
  title: string;
  content: string;
  category: string;
  files: File[];
}

interface NoticeProps {
  mode: 'create' | 'edit';
  onEditSuccess?: () => void;
}

interface ExistingFile {
  originalName: string;
  _id: string;
}

const Notice: React.FC<NoticeProps> = ({ mode, onEditSuccess }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileLimit = 5;

  const [newFiles, setNewFiles] = useState<File[]>([]); // 새로 선택한 파일
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]); // 서버에서 가져온 기존 파일
  const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]); // 삭제 예정 기존 파일 ID

  const quillRef = useRef<ReactQuill | null>(null);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<NoticeFormData>({
    mode: 'onSubmit',
    defaultValues: {
      user: user?.id || '',
      title: '',
      files: [],
      content: '',
      category: 'general',
    },
  });

  // ReactQuill 이미지 붙여넣기/드래그 방지
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const Delta = Quill.import('delta');
    editor.clipboard.addMatcher('IMG', () => new Delta());

    const root = editor.root as HTMLElement;
    const onPaste = (e: ClipboardEvent) => {
      const hasImage =
        !!e.clipboardData &&
        Array.from(e.clipboardData.items).some(
          item => item.kind === 'file' && item.type.startsWith('image/')
        );
      if (hasImage) e.preventDefault();
    };

    const onDrop = (e: DragEvent) => {
      const hasImage =
        !!e.dataTransfer && Array.from(e.dataTransfer.files).some(f => f.type.startsWith('image/'));
      if (hasImage) e.preventDefault();
    };

    root.addEventListener('paste', onPaste);
    root.addEventListener('drop', onDrop);

    return () => {
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('drop', onDrop);
    };
  }, []);

  // 수정 모드일 때 데이터 fetch
  useEffect(() => {
    if (mode === 'edit' && id) {
      const fetchNoticeDetail = async () => {
        try {
          const response = await axios.get(`/api/notice/${id}`);
          const data = response.data;
          reset({
            user: data.user,
            title: data.title,
            content: data.content,
            category: data.category || 'general',
            files: [],
          });
          setExistingFiles(data.fileList || []);
        } catch (error) {
          console.error('Error fetching notice detail:', error);
        }
      };
      fetchNoticeDetail();
    }
  }, [mode, id, reset]);

  // ReactQuill 내용 검증
  const validateQuillContent = (value: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.trim().length > 0 || '내용을 입력해주세요';
  };

  // 파일 삭제 핸들러
  const handleNewFileRemove = (index: number) => {
    const updated = [...newFiles];
    updated.splice(index, 1);
    setNewFiles(updated);
  };

  const handleExistingFileRemove = (id: string) => {
    setExistingFiles(prev => prev.filter(f => f._id !== id)); // UI에서 제거
    setDeletedFileIds(prev => [...prev, id]);
  };

  // 파일 입력 변경
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const combined = [...newFiles, ...files].slice(0, fileLimit);
    setNewFiles(combined);
  };

  const onSubmit = async (data: NoticeFormData) => {
    try {
      let uploadedIds: string[] = [];

      // 1) 새 파일 업로드
      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach(file => formData.append('files', file));
        formData.append('usedFor', 'notice');

        const uploadRes = await axios.post(`/api/upload-files/${'notice'}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedIds = uploadRes.data.fileIds;
      }

      // 2) 모든 파일 ID 합치기 (기존 + 새 업로드)
      const allFileIds = [...existingFiles.map(f => f._id), ...uploadedIds];

      // 3) 삭제된 파일 DB 처리 (deleted:true)
      if (deletedFileIds.length > 0) {
        deletedFileIds.forEach(async id => {
          try {
            await axios.put(`/api/upload-files/mark-deleted/${id}`, { deleted: true });
          } catch (err) {
            console.error(`Error deleting file ${id}:`, err);
          }
        });
      }

      // 4) Notice 등록/수정
      const payload = {
        user: user?.id || '',
        category: data.category,
        title: data.title,
        content: data.content,
        files: allFileIds,
        viewCount: 0,
      };

      if (mode === 'edit' && id) {
        await axios.put(`/api/notice/${id}`, payload);
        alert('공지사항이 수정되었습니다.');
      } else {
        await axios.post('/api/notice/create', payload);
        alert('공지사항이 등록되었습니다.');
      }

      onEditSuccess?.();
      navigate('/setting/notice');
    } catch (err) {
      console.error(err);
    }
  };

  const fileCount = existingFiles.length + newFiles.length;

  return (
    <div className="notice-container">
      <div className="page-header">
        <img src={icon} alt="Mind Thermometer Icon" className="page-header__icon" />
        <h2 className="page-header__title">
          {mode === 'edit' ? '공지사항 수정' : '공지사항 작성'}
        </h2>
      </div>
      <div className="notice">
        <form className="notice__form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="category">유형</label>
            <select id="category" {...register('category')}>
              <option value="general">일반</option>
              <option value="event">이벤트</option>
              <option value="update">업데이트</option>
              <option value="etc">기타</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="title">제목</label>
            <input
              type="text"
              id="title"
              placeholder="제목을 입력해주세요"
              {...register('title', { required: '제목을 입력해주세요' })}
            />
            {errors.title && <span className="error-message">{errors.title.message}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="content">내용</label>
            <div translate="no">
              <Controller
                name="content"
                control={control}
                rules={{ validate: validateQuillContent }}
                render={({ field }) => (
                  <ReactQuill
                    id="content"
                    className="quill-input"
                    theme="snow"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="내용을 입력해주세요"
                    ref={quillRef}
                  />
                )}
              />
            </div>
            {errors.content && <span className="error-message">{errors.content.message}</span>}
          </div>

          <div className="form-group">
            <div className="file-label-group">
              <label htmlFor="files">첨부파일</label>
              <span className="file-count">
                {fileCount}/{fileLimit}
              </span>
            </div>

            {fileCount < fileLimit && (
              <>
                <input
                  type="file"
                  id="files"
                  className="file-input"
                  multiple
                  onChange={handleFileChange}
                />
                <label htmlFor="files" className="file-upload-btn">
                  <div>
                    <img src={uploadBtn} alt="파일업로드" />
                    <span> {fileCount > 0 ? '파일 추가' : '파일 선택'}</span>
                  </div>
                  <img src={clip} alt="클립" />
                </label>
              </>
            )}

            <div className="file-list">
              {existingFiles.map(file => (
                <div className="file-item" key={file._id}>
                  <span className="file-item-name">
                    <img src={fileIcon} alt="업로드 파일" />
                    {file.originalName}
                  </span>
                  <span
                    className="file-item-remove"
                    onClick={() => handleExistingFileRemove(file._id)}
                  >
                    <img src={closeIcon} alt="파일 제거" />
                  </span>
                </div>
              ))}

              {newFiles.map((file, index) => (
                <div className="file-item" key={file.name + index}>
                  <span className="file-item-name">
                    <img src={fileIcon} alt="업로드 파일" />
                    {file.name}
                  </span>
                  <span className="file-item-remove" onClick={() => handleNewFileRemove(index)}>
                    <img src={closeIcon} alt="파일 제거" />
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            {mode === 'edit' ? (
              <button
                type="button"
                className="back-btn"
                onClick={() => {
                  navigate(`/setting/notice/${id}`);
                  onEditSuccess?.();
                }}
              >
                취소
              </button>
            ) : (
              <button
                type="button"
                className="back-btn"
                onClick={() => {
                  navigate(`/setting/notice`);
                  onEditSuccess?.();
                }}
              >
                취소
              </button>
            )}
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? <LoadingSpinner /> : mode === 'edit' ? '수정하기' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LoadingSpinner = () => {
  return <div className="loading-spinner"></div>;
};

export default Notice;
